/**
 * Payout Service
 * API calls for payout runs (cycle-based snapshots)
 */

import api from "@/shared/api";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Run monthly payout cycle for a payee
 * @param {Object} params
 * @param {string} params.payeeId - Payee ID (supplier/driver/partner)
 * @param {Object} [params.feeOverrides] - Optional fee overrides
 * @returns {Promise<Object>} Payout run result
 */
export const runMonthlyPayout = async ({ payeeId, feeOverrides }) => {
  try {
    if (!payeeId) {
      throw new Error("payeeId is required");
    }

    logInfo("Running monthly payout", { payeeId });

    const response = await api.post("/payouts/runs/cycle", {
      payeeId,
      feeOverrides: feeOverrides || null,
    });

    logInfo("Monthly payout run completed", {
      payoutRunId: response.data?.run?.id,
      payeeId,
    });

    return response;
  } catch (error) {
    logError("Failed to run monthly payout", {
      payeeId,
      error: error.message,
      status: error.response?.status,
    });
    throw error;
  }
};

/**
 * Get payout run by ID
 * @param {string} payoutRunId - Payout run ID
 * @returns {Promise<Object>} Payout run data with lines
 */
export const getPayoutRun = async (payoutRunId) => {
  try {
    if (!payoutRunId) {
      throw new Error("payoutRunId is required");
    }

    const response = await api.get(`/payouts/runs/${payoutRunId}`);

    return response;
  } catch (error) {
    logError("Failed to get payout run", {
      payoutRunId,
      error: error.message,
      status: error.response?.status,
    });
    throw error;
  }
};
